import axios from 'axios';
import moment from 'moment';
import jsCookie from 'js-cookie';
import pick from 'lodash/pick';
import cloneDeep from 'lodash/cloneDeep';

import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import { memoizeWithToken } from 'api/utils/memoizeUtils';
import {
  HEADERS,
  STORAGE_KEYS,
  USER_LAST_LOGIN,
  TEN_CALCULATED_SECONDS_IN_A_DAY
} from 'constants.js';

/* istanbul ignore file */

/** @TODO The user responses are being changed currently */
export function formatUserData({ data: { data, result, success } }) {
  const jwtPASSCODEToken = jsCookie.get(HEADERS.X_CM_PASSCODE);
  if (jwtPASSCODEToken) {
    const commonHeaders = axios.defaults.headers.common;
    delete commonHeaders[HEADERS.X_CM_PASSCODE];
  }
  if (!data.challeangeable && !success) {
    const error = data && data[0];
    const errorObject = { ...data[0], errorGuid: result.guid };
    if (error) throw errorObject;
  }

  return {
    success,
    name: data.userName,
    lastLogin: data.lastLoginTime,
    mustChangePassword: data.mustChangePassword,
    token: data.authenticationId || data.sessionId,
    errorMessage: result.systemMessage ? result.systemMessage : null,
    ...data
  };
}

/**
 * @description For displaying a logging out message
 * Clear session without immediately reloading.
 */
export function clearSession() {
  jsCookie.remove(STORAGE_KEYS.TOKEN);
  jsCookie.remove('INBOUND_SSO_SESSION', { path: '' });
  delete axios.defaults.headers.common[HEADERS.TOKEN];
}

export function nukeEntireSession() {
  jsCookie.remove(STORAGE_KEYS.TOKEN);
  // delete duplicate SSO token
  jsCookie.remove(STORAGE_KEYS.SSO_BFS_TOKEN_DUPE);
  // delete BFS cookie
  jsCookie.remove(STORAGE_KEYS.BFS_COOKIE);
  jsCookie.remove('INBOUND_SSO_SESSION', { path: '' });
  sessionStorage.clear();
  localStorage.removeItem(STORAGE_KEYS.EXPIRATION_SESSION_TIME);
  delete axios.defaults.headers.common[HEADERS.TOKEN];
}

export function checkDeviceTokens() {
  const storedTokens = JSON.parse(window.localStorage.getItem(HEADERS.DEVICE_TOKENS) || '[]');
  const commonHeaders = axios.defaults.headers.common;
  const now = moment();

  const tokens = storedTokens.filter(({ expires }) => moment(expires) >= now);
  window.localStorage.setItem(HEADERS.DEVICE_TOKENS, JSON.stringify(tokens));

  const tokensHeader = tokens.reduce(
    (obj, { deviceId, token }) => ({ ...obj, [deviceId]: token }),
    {}
  );
  commonHeaders[HEADERS.DEVICE_TOKENS] = JSON.stringify(tokensHeader);
}

export function setDeviceCode() {
  const commonHeaders = axios.defaults.headers.common;

  checkDeviceTokens();

  commonHeaders[HEADERS.DEVICE_CODE] = window.localStorage.getItem(HEADERS.DEVICE_CODE);
  commonHeaders[HEADERS.DEVICE_TOKEN] = window.localStorage.getItem(HEADERS.DEVICE_TOKEN);
}

/**
 * @description to avoid multiple *imidiate* calls, we cache the promise.
 */
let currentUser;
export function fetchUserData(reload) {
  const tabToken = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
  const inboundSessionToken = jsCookie.get('INBOUND_SSO_SESSION');
  const jwtToken = jsCookie.get(STORAGE_KEYS.TOKEN) || tabToken || inboundSessionToken;
  const commonHeaders = axios.defaults.headers.common;

  /** This next block will never run since we only use fetchUserData
   *  in the Bootstrap.js file. So currentUser will never be defined
   * or accessed again.
   */
  /* istanbul ignore next */
  if (!reload && jwtToken && currentUser && currentUser.token === jwtToken) {
    return currentUser;
  }

  setDeviceCode();

  if (commonHeaders[HEADERS.TOKEN] !== jwtToken) {
    commonHeaders[HEADERS.TOKEN] = `Bearer ${jwtToken}`;
  }

  currentUser = axios.post('/authentication/session/getUserSessionInfo').then(formatUserData);
  return currentUser;
}

export function getUserSessionInfo(token) {
  return from(
    axios.post('/authentication/session/getUserSessionInfo', null, {
      Authorization: `Bearer ${token}`
    })
  ).pipe(map(({ data: { data, success } }) => ({ success, data })));
}

const sanitizeDeviceModel = (deviceModel) =>
  deviceModel
    // iPhone XR specific replacing unicode small R
    .replace(/ʀ/g, 'R')
    // strip out any non-ascii chars to prevent similar issues
    /* eslint-disable no-control-regex*/
    .replace(/[^\u0000-\u007F]/g, '');

export function login({
  customerCode,
  isNative = false,
  password: userPassword,
  redirectToken,
  userCode
}) {
  const jwtPASSCODEToken = jsCookie.get(HEADERS.X_CM_PASSCODE);
  const commonHeaders = axios.defaults.headers.common;
  if (jwtPASSCODEToken) {
    commonHeaders[HEADERS.X_CM_PASSCODE] = jwtPASSCODEToken;
  } else {
    delete commonHeaders[HEADERS.X_CM_PASSCODE];
  }
  if (!commonHeaders[HEADERS.TOKEN]) {
    axios.defaults.headers.common[HEADERS.TOKEN] = 'Bearer null';
  }
  const { deviceId = '', deviceModel = '' } = window?.BOTTOMLINE?.NATIVE_PARAMS || {};
  const truncUserCode = userCode.substring(0, 12);
  const truncCustomerCode = customerCode.substring(0, 12);
  const deviceInfoHeader = isNative
    ? {
        headers: {
          'X-DEVICE-INFO': JSON.stringify({
            deviceId,
            deviceModel: sanitizeDeviceModel(deviceModel)
          })
        }
      }
    : {};

  return axios
    .post(
      '/authentication/public/session/login',
      {
        userCode: truncUserCode,
        userPassword,
        customerCode: truncCustomerCode,
        redirectToken
      },
      deviceInfoHeader
    )
    .then(formatUserData)
    .then((data) => {
      /**
       * @prop {boolean} hasUserEstablishedSecurityQuestions
       * @prop {boolean} passwordExpired
       * */
      window.localStorage.setItem(USER_LAST_LOGIN, data.lastLoginTime);
      sessionStorage.setItem(STORAGE_KEYS.TOKEN, data.token);
      jsCookie.set(STORAGE_KEYS.TOKEN, data.token, { expires: 1 });
      axios.defaults.headers.common[HEADERS.TOKEN] = `Bearer ${data.token}`;
      return {
        /** @todo the response missing the 'userCode', 'customerCode', 'termsAndConditions'
         * fixing here for the demo. will be overridden if value does come back */
        userCode: truncUserCode,
        customerCode: truncCustomerCode,
        termsAndConditions: true,
        /** @end */
        ...data
      };
    });
}

export function startUserEmulation(payload) {
  return from(axios.post('/authentication/emulation/startEmulation', payload)).pipe(
    map(({ data: { data } }) => {
      jsCookie.set(STORAGE_KEYS.TOKEN, data.authenticationId, {
        expires: TEN_CALCULATED_SECONDS_IN_A_DAY
      });
      axios.defaults.headers.common[HEADERS.TOKEN] = `Bearer ${data.authenticationId}`;
      return data;
    })
  );
}

export function logout({ history, isSessionExpired = false } = {}) {
  jsCookie.remove('INBOUND_SSO_SESSION', { path: '' });
  return axios.post('/authentication/session/logoff', { isSessionExpired }).then(({ data }) => {
    if (isSessionExpired) {
      sessionStorage.clear();
      jsCookie.remove(STORAGE_KEYS.TOKEN);
    } else {
      const { emulatorSessionId: token, logOffURL } = data.data || {};
      if (token) {
        if (isSessionExpired) {
          sessionStorage.setItem(STORAGE_KEYS.TOKEN, token);
        }
        jsCookie.set(STORAGE_KEYS.TOKEN, token);
        axios.defaults.headers.common[HEADERS.TOKEN] = `Bearer ${token}`;
      } else {
        jsCookie.remove(STORAGE_KEYS.TOKEN);
        const preserve = {
          infuse: window.sessionStorage.getItem('infuse')
        };
        window.sessionStorage.clear();
        window.localStorage.removeItem(STORAGE_KEYS.EXPIRATION_SESSION_TIME);

        // preserve some keys in session
        if (preserve.infuse) {
          const o = cloneDeep(JSON.parse(preserve.infuse));
          Object.keys(o).forEach((x) => {
            o[x] = { closed: +!!o[x].completed };
          });
          window.sessionStorage.setItem('infuse', JSON.stringify(o));
        }

        jsCookie.remove(HEADERS.X_CM_PASSCODE);
        delete axios.defaults.headers.common[HEADERS.X_CM_PASSCODE];
        delete axios.defaults.headers.common[HEADERS.TOKEN];

        if (logOffURL) {
          try {
            const url = new URL(logOffURL);
            window.location.replace(url);
          } catch {
            history?.replace(logOffURL, {});
          }
        }
      }
    }
    return data;
  });
}

export function fetchUserSecurityInfo(viewType) {
  return from(
    axios.post('/authentication/user/retrievesUserSecurityInfo', {
      viewType
    })
  ).pipe(map(({ data }) => data));
}

export function updateUserInfo(data) {
  const jwtPASSCODEToken = jsCookie.get(HEADERS.X_CM_PASSCODE);
  const commonHeaders = axios.defaults.headers.common;
  if (jwtPASSCODEToken) {
    commonHeaders[HEADERS.X_CM_PASSCODE] = jwtPASSCODEToken;
  }
  const payload = pick(data, [
    'address1',
    'address2',
    'city',
    'customerCode',
    'email',
    'faxNumber',
    'phoneNumber',
    'postalCode',
    'state',
    'userCode',
    'userName'
  ]);
  return from(axios.post('/authentication/user/updateUserInfo', payload)).pipe(
    map(({ data }) => {
      delete axios.defaults.headers.common[HEADERS.X_CM_PASSCODE];
      return data;
    })
  );
}

/**
 * @function updatesSecurityContactInfo
 * @param { object[] } securityContacts
 * @param { "MAINTENANCE" | "ENROLL" } viewType
 */
export function updatesSecurityContactInfo(securityContacts, viewType) {
  const jwtPASSCODEToken = jsCookie.get(HEADERS.X_CM_PASSCODE);
  const commonHeaders = axios.defaults.headers.common;
  if (jwtPASSCODEToken) {
    commonHeaders[HEADERS.X_CM_PASSCODE] = jwtPASSCODEToken;
  }
  return from(
    axios.post('/authentication/user/updatesSecurityContactInfo', {
      securityContacts,
      viewType
    })
  ).pipe(
    map(({ data }) => {
      delete axios.defaults.headers.common[HEADERS.X_CM_PASSCODE];
      return data;
    })
  );
}

export const updatesSecurityContactInfo$ = (payload) =>
  from(axios.post('/authentication/user/updatesSecurityContactInfo', payload)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );

/**
 * @function sendActivationCode
 * @param {Contact} contact
 * @prop  {string} contactType: "SMS",
 * @prop  {string} value: "2223334444",
 * @prop  {boolean} default: true,
 * @value
 {
    "contactType": "SMS",
    "default": true,
    "value": "7609607096"
  }
 */
export function sendActivationCode(contact) {
  return axios.post('/authentication/user/sendActivationCode', [contact]).then(({ data }) => data);
}

export const sendActivationCode$ = (payload) =>
  from(axios.post('/authentication/user/sendActivationCode', payload)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );

export function validateActivationCode(activationCode) {
  return axios
    .post('/authentication/user/validateActivationCode', activationCode)
    .then(({ data }) => data);
}

export const validateActivationCode$ = (payload) =>
  from(axios.post('/authentication/user/validateActivationCode', payload)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );

export function checkMFA2Enabled() {
  return from(axios.post('/authentication/user/checkMFA2Enabled')).pipe(map(({ data }) => data));
}

export function updateFirstTimeLoginStatus() {
  return from(axios.post('/authentication/user/updateFirstTimeLoginStatus')).pipe(
    map(({ data }) => data)
  );
}

export const updateFirstTimeLoginStatus$ = () =>
  from(axios.post('/authentication/user/updateFirstTimeLoginStatus')).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );

export function getSessionTimeRemaining() {
  return from(axios.post('/authentication/session/getSessionTimeRemaining')).pipe(
    map(({ data }) => data)
  );
}

export function extendSession() {
  return from(axios.post('/authentication/session/extend')).pipe(map(({ data }) => data));
}

export function refreshSession() {
  return from(axios.post('/authentication/session/refresh')).pipe(map(({ data }) => data));
}

export function sendOTPCode(payload) {
  return from(axios.post('authentication/user/sendOTPCode', payload)).pipe(
    map(({ data: { data, success } }) => ({ data, success }))
  );
}

export const setSessionData = () => {};
